import { CreditOffer, DepositsOffer, LifeInsuranceOffer, LoanOffer, Offer } from 'embed/constants/models'
import {
  CreditSortOptionUI,
  DepositsSortOption,
  LifeInsuranceSortOption,
  LoanSortOption,
  Product,
  SortDirection,
  SortOption,
  TermUnit,
} from '@evenfinancial/even-ts-static'

type AttributeTypeAtPath<ObjectType, Path extends string, DefaultType> = Path extends keyof ObjectType
  ? ObjectType[Path]
  : Path extends `${infer LeftSide}.${infer RightSide}`
  ? LeftSide extends keyof ObjectType
    ? AttributeTypeAtPath<ObjectType[LeftSide], RightSide, DefaultType>
    : DefaultType
  : DefaultType

const getNestedObjectValue = <T extends {}, P extends string, D = undefined>(
  obj: T,
  nestedKey: P,
): AttributeTypeAtPath<T, P, D> => {
  let value
  if (nestedKey) {
    value = obj
    for (const key of nestedKey.split('.')) {
      value = value?.[key]
      if (value === undefined) {
        break
      }
    }
  }
  return value as AttributeTypeAtPath<T, P, D>
}
const getSortDirection = (direction: SortDirection) => (direction === SortDirection.Desc ? -1 : 1)

type Iteratee = (item: Offer) => string | undefined | number
const orderBy = <OfferType extends Offer, OfferArray extends Readonly<OfferType[]>>(
  collection: OfferArray,
  props: string[] | Iteratee[],
  direction: SortDirection[],
): OfferArray => {
  if (!collection) {
    return [] as unknown as OfferArray
  } else if (!collection.length) {
    return collection
  }
  return ([...collection] as OfferType[]).sort((a, b) => {
    for (const [index, prop] of props.entries()) {
      const sortDirection = getSortDirection(direction[index])
      let firstEl
      let secondEl
      if (typeof prop === 'function') {
        firstEl = prop(a)
        secondEl = prop(b)
      } else if (prop.includes('.')) {
        firstEl = getNestedObjectValue(a, prop)
        secondEl = getNestedObjectValue(b, prop)
      } else {
        firstEl = a[prop]
        secondEl = b[prop]
      }
      if (firstEl !== secondEl) {
        // put null values after everything else
        if (firstEl === null) {
          return 1
        } else if (secondEl === null) {
          return -1
        }
        return firstEl > secondEl ? sortDirection : -sortDirection
      }
    }
    return 0
  }) as unknown as OfferArray
}

// Helper function to use single function and just pass the product
export const sortOffers = <OfferType extends Offer, OfferArray extends Readonly<OfferType[]>>(
  product: Product,
  offers: OfferArray,
  sort: SortOption,
  direction: SortDirection,
  loanAmount?: number,
): OfferArray => {
  let sortedOffers
  switch (product) {
    case Product.LifeInsurance:
      sortedOffers = sortLifeInsuranceOffers(offers as Readonly<LifeInsuranceOffer[]>, sort, direction)
      break
    case Product.Loan:
      sortedOffers = sortLoanOffers(offers as Readonly<LoanOffer[]>, sort, direction, loanAmount)
      break
    case Product.Deposits:
      sortedOffers = sortDepositsOffers(offers as Readonly<DepositsOffer[]>, sort, direction)
      break
    case Product.CreditCard:
      sortedOffers = sortCreditCardOffers(offers as Readonly<CreditOffer[]>, sort, direction)
      break
    default:
      sortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  }

  return sortedOffers
}

export const sortLoanOffers = <OfferArray extends Readonly<LoanOffer[]>>(
  offers: OfferArray,
  sort: SortOption,
  direction: SortDirection,
  loanAmount?: number,
  useConversionProbability = false,
): OfferArray => {
  if (!offers || !offers.length) {
    return offers
  }

  let sortedOffers
  const preSortedOfferSortOption = useConversionProbability ? 'conversionProbability' : 'recommendationScore'
  const presortedOffers = orderBy(offers, [preSortedOfferSortOption], [SortDirection.Desc])

  switch (sort) {
    case LoanSortOption.Lender:
      const loanSortLender = orderBy(
        presortedOffers,
        [(offer: LoanOffer) => offer.originator?.name?.toLowerCase()],
        [direction],
      )
      sortedOffers = loanSortLender
      break
    case LoanSortOption.Term: {
      const loanSortTerm = orderBy(
        presortedOffers,
        [
          (offer: LoanOffer) => {
            const termLength = offer.termLength?.valueOf() || 0
            switch (offer.termUnit) {
              case TermUnit.Days:
                return termLength / 30
              case TermUnit.Years:
                return termLength * 12
              default:
                return termLength
            }
          },
        ],
        [direction],
      )
      sortedOffers = loanSortTerm
      break
    }
    case LoanSortOption.Apr: {
      const loanSortApr = orderBy(presortedOffers, ['meanApr'], [direction])
      sortedOffers = loanSortApr
      break
    }
    case LoanSortOption.Payment:
      const loanSortPayment = orderBy(
        presortedOffers,
        [(offer: LoanOffer) => offer.meanMonthlyPayment || offer.monthlyPayment],
        [direction],
      )
      sortedOffers = loanSortPayment
      break
    case LoanSortOption.Amount:
      sortedOffers = orderBy(presortedOffers, ['maxAmount'], [direction])
      break
    case LoanSortOption.Recommended:
    case LoanSortOption.Relevance:
      sortedOffers = orderBy(presortedOffers, [preSortedOfferSortOption], [direction])
      break
    case LoanSortOption.ConversionProbability:
      sortedOffers = orderBy(presortedOffers, ['conversionProbability'], [direction])
      break
    case LoanSortOption.ClosestAmount:
      const amount = loanAmount || 0
      sortedOffers = [...presortedOffers].sort((a: LoanOffer, b: LoanOffer) => {
        const aValue = Math.abs(amount - (a?.maxAmount ?? 0))
        const bValue = Math.abs(amount - (b?.maxAmount ?? 0))

        if (aValue === bValue) {
          return 0
        } else if (direction === 'asc') {
          return aValue < bValue ? -1 : 1
        } else {
          return aValue > bValue ? -1 : 1
        }
      })
      break
    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortDepositsOffers = <OfferArray extends Readonly<DepositsOffer[]>>(
  offers: OfferArray,
  sort: SortOption,
  direction: SortDirection,
): OfferArray => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers
  switch (sort) {
    // Deposits Sort Options
    case DepositsSortOption.APY:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: DepositsOffer) => offer.details?.annualPercentYield],
        [direction],
      )
      break
    case DepositsSortOption.Minimum:
      sortedOffers = orderBy(presortedOffers, [(offer: DepositsOffer) => offer.details?.minimumDeposit], [direction])
      break
    case DepositsSortOption.Partner:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: DepositsOffer) => offer.partner?.name?.toLocaleLowerCase()],
        [direction],
      )
      break
    case DepositsSortOption.Recommended:
      sortedOffers = orderBy(presortedOffers, ['recommendationScore'], [direction])
      break

    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortCreditCardOffers = <OfferArray extends Readonly<CreditOffer[]>>(
  offers: OfferArray,
  sort: SortOption,
  direction: SortDirection,
): OfferArray => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers

  switch (sort) {
    // Credit Card Sorts
    case CreditSortOptionUI.Recommended:
      sortedOffers = orderBy(
        presortedOffers,
        ['recommendationScore', 'details.minPurchaseApr'],
        [direction, SortDirection.Asc],
      )

      break
    case CreditSortOptionUI.Rewards:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.earningProgram?.personalization?.totalValue ?? 0],
        [direction],
      )
      break
    case CreditSortOptionUI.Name:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.cardName?.toLowerCase()],
        [direction],
      )
      break
    case CreditSortOptionUI.VariableApr:
      sortedOffers = orderBy(presortedOffers, [(offer: CreditOffer) => offer.details.minPurchaseApr], [direction])
      break
    case CreditSortOptionUI.AnnualFee:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.maxAnnualFee || offer.details.minAnnualFee],
        [direction],
      )
      break
    case CreditSortOptionUI.IntroApr:
      sortedOffers = orderBy(presortedOffers, [(offer: CreditOffer) => offer.details?.minPurchaseIntroApr], [direction])
      break

    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortLifeInsuranceOffers = <OfferArray extends Readonly<LifeInsuranceOffer[]>>(
  offers: OfferArray,
  sort: SortOption,
  direction: SortDirection,
): OfferArray => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers
  switch (sort) {
    // Life Insurance Sorts
    case LifeInsuranceSortOption.Recommended:
      const recommendedOffers = presortedOffers.filter((offer: LifeInsuranceOffer) => offer.details?.recommended)
      const nonrecommendedOffers = presortedOffers.filter((offer: LifeInsuranceOffer) => !offer.details?.recommended)

      const sortedRecommendedOffers = orderBy(recommendedOffers, ['details.monthlyPremiumAmount'], [SortDirection.Asc])
      const sortedNonrecommendedOffers = orderBy(nonrecommendedOffers, ['recommendationScore'], [SortDirection.Desc])
      sortedOffers = [...sortedRecommendedOffers, ...sortedNonrecommendedOffers]

      if (direction === SortDirection.Asc) {
        sortedOffers = sortedOffers.reverse()
      }

      break
    case LifeInsuranceSortOption.PolicyAmount:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.policyAmount', 'details.monthlyPremiumAmount'],
        [direction, SortDirection.Asc],
      )
      break
    case LifeInsuranceSortOption.TermLength:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.termLength', 'details.monthlyPremiumAmount'],
        [direction, SortDirection.Asc],
      )
      break
    case LifeInsuranceSortOption.MonthlyPremiumAmount:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.monthlyPremiumAmount', 'recommendationScore'],
        [direction, SortDirection.Desc],
      )
      break
    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const groupOffersByLenderId = <OfferArray extends Readonly<Offer[]>>(
  offers: OfferArray | undefined | null,
  key: string,
) => {
  if (!offers?.length) {
    return {}
  }
  const [offerKey, offerSubkey] = key.split('.')
  return offers.reduce((groupedOffers, offer) => {
    const lenderIdKey = offerSubkey ? offer[offerKey][offerSubkey] : offer[offerKey]
    groupedOffers[lenderIdKey] = [...(groupedOffers[lenderIdKey] ?? []), offer]
    return groupedOffers
  }, {})
}
